import type {
  PublicationImportProductOutcomeOut,
  PublicationImportProductsOut,
} from '@/api/generated/schemas';
import { ProductImportStatus } from '@/api/generated/schemas';
import type { OutcomeTone } from '@/components/common/OutcomeTone';

export const BATCH_SIZE = 20;

export const batches = (ids: number[], size = BATCH_SIZE): number[][] => {
  const out: number[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
};

const EMPTY: PublicationImportProductsOut = {
  requested: 0,
  imported: 0,
  fused: 0,
  already_imported: 0,
  publication_imported_product_missing: 0,
  product_dropped: 0,
  blocked: 0,
  skipped: 0,
  failed: 0,
  unknown_product: 0,
  outcomes: [],
};

export const mergeResults = (
  results: PublicationImportProductsOut[]
): PublicationImportProductsOut =>
  results.reduce(
    (acc, r) => ({
      requested: acc.requested + r.requested,
      imported: acc.imported + r.imported,
      fused: acc.fused + r.fused,
      already_imported: acc.already_imported + r.already_imported,
      publication_imported_product_missing:
        acc.publication_imported_product_missing +
        r.publication_imported_product_missing,
      product_dropped: acc.product_dropped + r.product_dropped,
      blocked: acc.blocked + r.blocked,
      skipped: acc.skipped + r.skipped,
      failed: acc.failed + r.failed,
      unknown_product: acc.unknown_product + r.unknown_product,
      outcomes: [...acc.outcomes, ...r.outcomes],
    }),
    EMPTY
  );

/** The two statuses that mean a local row now exists. */
export const isSuccessStatus = (status: ProductImportStatus): boolean =>
  status === ProductImportStatus.imported ||
  status === ProductImportStatus.fused;

/**
 * How many products the admin still has to do something about.
 *
 * `skipped` counts: it is what a refused fusion, a non-importable language or
 * publishing status, and a foreign exclusivity all come back as — nothing was
 * written and the operator decides what happens next. `already_imported` is
 * the only benign non-success: the row was already where it should be.
 */
export const problemCount = (result: PublicationImportProductsOut): number =>
  result.blocked +
  result.failed +
  result.unknown_product +
  result.publication_imported_product_missing +
  result.product_dropped +
  result.skipped;

/** Share of the batch that produced a local row, 0–100. */
export const successPercent = (result: PublicationImportProductsOut): number =>
  result.requested
    ? Math.round(((result.imported + result.fused) / result.requested) * 100)
    : 0;

/** The local publication this product ended up on, created or pre-existing. */
export const linkedPublicationId = (
  outcome: PublicationImportProductOutcomeOut
): number | null =>
  outcome.user_catalog_publication_id ?? outcome.fused_into ?? null;

export const statusTone = (status: ProductImportStatus): OutcomeTone => {
  switch (status) {
    case ProductImportStatus.imported:
    case ProductImportStatus.fused:
      return 'success';
    // `skipped` sits here too: nothing was written and the reason is the
    // operator's to act on, so it is painted like the rest of what
    // `problemCount` counts.
    case ProductImportStatus.blocked:
    case ProductImportStatus.product_dropped:
    case ProductImportStatus.publication_imported_product_missing:
    case ProductImportStatus.skipped:
      return 'warning';
    case ProductImportStatus.failed:
      return 'error';
    default:
      return 'neutral';
  }
};

/** Nothing happened and nothing needs to: dim the row. */
export const isMutedStatus = (status: ProductImportStatus): boolean =>
  status === ProductImportStatus.already_imported ||
  status === ProductImportStatus.unknown_product;

const NS = 'user_catalog.publications.import_products';

/** i18next returns the key itself when nothing is registered under it. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Both vocabularies below are closed backend enums, but they grow ahead of
 * this panel — an unmapped value prints raw rather than as a key path.
 */
const translated = (
  t: Translate,
  key: string,
  raw: string,
  options?: Record<string, unknown>
) => {
  const value = t(key, options);
  return value === key ? raw : value;
};

export const statusText = (t: Translate, status: ProductImportStatus): string =>
  translated(t, `${NS}.result.status.${status}`, status);

export const reasonText = (
  t: Translate,
  code: string | null | undefined,
  context?: Record<string, unknown>
): string | undefined =>
  code
    ? translated(t, `${NS}.reasons.${code}`, code, context ?? {})
    : undefined;
